import React, { useState, useMemo } from 'react';
import {
  BookOpen,
  Sparkles,
  ShieldCheck,
  Coins,
  Clock,
  CheckCircle,
  AlertTriangle,
  FileText,
} from 'lucide-react';
import { SCRIPTURE_PRESETS } from '../../mockData';
import { SafetyCheckResult, ScriptureCitation, UseCase } from '../../types';

interface ScriptEditorProps {
  script: string;
  onChange: (value: string) => void;
  selectedUseCase: UseCase;
  onSelectUseCase: (useCase: UseCase) => void;
  creditMultiplier?: number;
  onValidationChange?: (result: SafetyCheckResult | null) => void;
}

const CANONICAL_BOOKS = [
  'Genesis',
  'Exodus',
  'Leviticus',
  'Numbers',
  'Deuteronomy',
  'Joshua',
  'Judges',
  'Ruth',
  '1 Samuel',
  '2 Samuel',
  '1 Kings',
  '2 Kings',
  '1 Chronicles',
  '2 Chronicles',
  'Ezra',
  'Nehemiah',
  'Esther',
  'Job',
  'Psalm',
  'Psalms',
  'Proverbs',
  'Ecclesiastes',
  'Song of Solomon',
  'Isaiah',
  'Jeremiah',
  'Lamentations',
  'Ezekiel',
  'Daniel',
  'Hosea',
  'Joel',
  'Amos',
  'Obadiah',
  'Jonah',
  'Micah',
  'Nahum',
  'Habakkuk',
  'Zephaniah',
  'Haggai',
  'Zechariah',
  'Malachi',
  'Matthew',
  'Mark',
  'Luke',
  'John',
  'Acts',
  'Romans',
  '1 Corinthians',
  '2 Corinthians',
  'Galatians',
  'Ephesians',
  'Philippians',
  'Colossians',
  '1 Thessalonians',
  '2 Thessalonians',
  '1 Timothy',
  '2 Timothy',
  'Titus',
  'Philemon',
  'Hebrews',
  'James',
  '1 Peter',
  '2 Peter',
  '1 John',
  '2 John',
  '3 John',
  'Jude',
  'Revelation',
];

export const ScriptEditor: React.FC<ScriptEditorProps> = ({
  script,
  onChange,
  onSelectUseCase,
  creditMultiplier = 1.0,
  onValidationChange,
}) => {
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<SafetyCheckResult | null>(null);

  // Real-time Scripture reference detection
  const detectedScriptures = useMemo(() => {
    const list: string[] = [];
    if (!script) return list;

    for (const book of CANONICAL_BOOKS) {
      const regex = new RegExp(`\\b${book}\\s+(\\d+):(\\d+)(?:-(\\d+))?`, 'gi');
      let match;
      while ((match = regex.exec(script)) !== null) {
        if (!list.includes(match[0])) {
          list.push(match[0]);
        }
      }
    }
    return list;
  }, [script]);

  const characterCount = script.length;
  const wordCount = script.trim() ? script.trim().split(/\s+/).length : 0;
  const estimatedSeconds = Math.max(1, Math.round(wordCount / 2.5)); // ~150 wpm = 2.5 words/sec

  // Calculate friendly credit burn (characters * multiplier)
  const estimatedCreditsBurned = useMemo(() => {
    if (!characterCount) return 0;
    return Math.max(10, Math.round(characterCount * (creditMultiplier || 1.0)));
  }, [characterCount, creditMultiplier]);

  const runPreflightSafetyCheck = () => {
    setIsValidating(true);
    setTimeout(() => {
      const lower = script.toLowerCase();
      const hasProhibited =
        lower.includes('blasphemy') || lower.includes('hate') || lower.includes('kill');

      const scriptures: ScriptureCitation[] = detectedScriptures.map((s) => {
        const parts = s.split(' ');
        const cv = parts[parts.length - 1].split(':');
        return {
          book: parts.slice(0, parts.length - 1).join(' '),
          chapter: parseInt(cv[0], 10),
          verseRange: cv[1] || '1',
          textSnippet: s,
        };
      });

      const result: SafetyCheckResult = {
        isValid: !hasProhibited,
        score: hasProhibited ? 15 : 98,
        scripturesDetected: scriptures,
        warnings:
          characterCount > 2500
            ? ['Script length exceeds recommended 3-minute pastoral segment.']
            : [],
        reasons: hasProhibited
          ? ['Content contains prohibited or unbiblical harmful terminology.']
          : [],
      };

      setValidationResult(result);
      setIsValidating(false);
      if (onValidationChange) onValidationChange(result);
    }, 450);
  };

  return (
    <div className="space-y-3">
      {/* Script Header Bar: Clear Context & Optional Presets */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <span className="text-xs text-slate-600 dark:text-slate-400 font-medium">
          Enter or paste scripture reading, pastoral reflection, or sermon recap:
        </span>

        {/* Quick Presets Dropdown */}
        <select
          onChange={(e) => {
            const selected = SCRIPTURE_PRESETS.find((p) => p.title === e.target.value);
            if (selected) {
              onChange(selected.text);
              onSelectUseCase(selected.useCase);
              setValidationResult(null);
            }
          }}
          defaultValue=""
          className="bg-slate-100 border border-slate-200 text-xs text-slate-800 rounded-xl px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 cursor-pointer self-start sm:self-auto dark:bg-slate-900 dark:border-slate-800 dark:text-slate-300 transition-colors"
        >
          <option value="" disabled>
            Load Sample Scripture...
          </option>
          {SCRIPTURE_PRESETS.map((p) => (
            <option key={p.title} value={p.title}>
              {p.title}
            </option>
          ))}
        </select>
      </div>

      {/* Main Textarea */}
      <div className="relative rounded-2xl bg-slate-50 border border-slate-200 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500/20 dark:bg-slate-900/80 dark:border-slate-800 dark:focus-within:border-indigo-500/50 dark:focus-within:ring-indigo-500/30 transition-all shadow-inner">
        <textarea
          rows={8}
          value={script}
          onChange={(e) => {
            onChange(e.target.value);
            if (validationResult) setValidationResult(null);
          }}
          placeholder="Enter sermon recap, devotional meditation, or verbatim biblical passage here..."
          className="w-full bg-transparent px-4 py-3.5 text-sm text-slate-900 placeholder-slate-400 dark:text-slate-100 dark:placeholder-slate-500 focus:outline-none resize-y leading-relaxed font-sans"
        />

        {/* Detected Scripture References Ribbon */}
        {detectedScriptures.length > 0 && (
          <div className="px-4 py-2.5 border-t border-slate-200 bg-indigo-50/50 rounded-b-2xl flex flex-wrap items-center gap-2 dark:border-slate-800/60 dark:bg-slate-950/40 transition-colors">
            <span className="text-[11px] text-indigo-600 dark:text-indigo-400 font-semibold flex items-center gap-1">
              <BookOpen className="w-3.5 h-3.5" />
              Detected Scripture:
            </span>
            {detectedScriptures.map((citation) => (
              <span
                key={citation}
                className="text-[11px] font-mono px-2.5 py-0.5 rounded-full bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border border-indigo-500/30 font-semibold"
              >
                {citation}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Metrics Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-slate-600 bg-slate-100/70 border border-slate-200 p-3.5 rounded-2xl dark:text-slate-400 dark:bg-slate-900/50 dark:border-slate-800/60 transition-colors">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5 font-medium">
            <FileText className="w-3.5 h-3.5 text-slate-400" />
            <span>{characterCount.toLocaleString()} chars</span>
            <span className="text-slate-300 dark:text-slate-600">•</span>
            <span>{wordCount} words</span>
          </div>
          <div className="flex items-center gap-1.5 font-medium">
            <Clock className="w-3.5 h-3.5 text-slate-400" />
            <span>~{estimatedSeconds}s audio</span>
          </div>
          <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 font-bold">
            <Coins className="w-3.5 h-3.5" />
            <span>🪙 {estimatedCreditsBurned.toLocaleString()} Credits</span>
          </div>
        </div>

        {/* Pre-Flight Safety Verification Trigger */}
        <button
          onClick={runPreflightSafetyCheck}
          disabled={isValidating || !script.trim()}
          className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-xl bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-200 dark:border-slate-700 text-xs font-semibold transition-all disabled:opacity-50 cursor-pointer"
        >
          {isValidating ? (
            <>
              <Sparkles className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400 animate-spin" />
              <span>Checking Scripture...</span>
            </>
          ) : (
            <>
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
              <span>Verify Theological Safety</span>
            </>
          )}
        </button>
      </div>

      {/* Safety & Theological Integrity Feedback Banner */}
      {validationResult && (
        <div
          className={`p-3.5 rounded-2xl border flex items-start gap-3 transition-all ${
            validationResult.isValid
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-950/30 dark:border-emerald-500/30 dark:text-emerald-300'
              : 'bg-rose-50 border-rose-200 text-rose-800 dark:bg-rose-950/40 dark:border-rose-500/40 dark:text-rose-300'
          }`}
        >
          {validationResult.isValid ? (
            <CheckCircle className="w-5 h-5 text-emerald-500 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-rose-500 dark:text-rose-400 flex-shrink-0 mt-0.5" />
          )}
          <div className="space-y-1">
            <div className="font-bold text-xs">
              {validationResult.isValid
                ? `Theological Safety Verified (Score: ${validationResult.score}/100)`
                : 'Safety Violations Detected'}
            </div>
            <p className="text-[11px] leading-relaxed">
              {validationResult.isValid
                ? 'Scripture integrity confirmed. No doctrinal distortions, profanity, or unauthorized impersonation flags found.'
                : validationResult.reasons.join(' ')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
